import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Modal,
  Fade,
  Backdrop,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from "@mui/icons-material";
import useThemeStore from "@/shared/stores/useThemeStore";
import { NewsItem, TalentExperienceData } from "../../types";
import { useTranslation } from "react-i18next";
import { useCurrentTalentProfile } from "@/shared";
import { useAuth } from "@/shared/stores/useAuth";
import { USER_TYPES } from "@/shared/constants";
import LazyTalentExperienceDisplayReadOnly from "./LazyTalentExperienceDisplayReadOnly";
import { isDemoEpisodeReleased } from "@/cards/demo-card/utils/demoCardPreviewUtils";
import { isSeasonSeriesReleased } from "@/cards/season-card/utils/seasonCardPreviewUtils";
import useSealTimeGate from "@/cards/common/utils/useSealTimeGate";
import DeckCardGuestLoginDialog from "@/cards/common/component/deck-library/DeckCardGuestLoginDialog";

import {
  getFanThankYouDisplayName,
  isCardSealed,
} from "@/cards/single-album-card/component/SingleCardFanThankYouPanel";
import { CardPageContent } from "./CardPageContent";
import { buildCardTocPages } from "./CardTocDrawer";
import { CardTocPage } from "./CardTocPage";
import { CARD_ASPECT_RATIO, CARD_ASPECT_RATIO_CSS } from "@/shared/constants/cardLayout";
import ImpactCardBookPage, {
  getImpactCardBookPageOrder,
  type ImpactCardBookSection,
} from "@/cards/impact-card/components/ImpactCardBookPage";
import {
  getImpactCardData,
  isInvitedImpactPartnerViewer,
} from "@/cards/impact-card/utils/impactCardData";
import {
  IMPACT_CARD_TYPE,
  isImpactCardCreator,
  isImpactCardType,
  readImpactCardCreatorTalentId,
} from "@/cards/impact-card/utils/impactCardType";
import ImpactCubePage, {
  findCubeForSection,
  ImpactStackedPage,
} from "@/cards/impact-card/components/ImpactCubePage";
import { AnchorCubeProvider, useAnchorCubes } from "@/cards/impact-card/hooks/useAnchorCubes";
import { COLLECTOR_ONLY_SECTIONS, CREATOR_ONLY_SECTIONS, cubeSectionKey } from "@/cards/impact-card/utils/anchorCubes";
import { cubeFromSubpages, flattenImpactPages, findImpactShellPageId, isImpactAnchorPage, isImpactSubpage, scrollImpactBookToTop, scrollImpactColumnIntoView, uniqueColumns } from "@/cards/impact-card/utils/impactPages";

/** Marks the poster page injected as the 2nd page of every Impact Card. */
const IMPACT_POSTER_SECTION = "poster" as const;

interface PreviewPage {
  id?: number;
  order: number;
  type: string;
  content: any;
  assets: number[];
  created_at?: string;
}

/** CoverImageDisplay book page is kept in code but not shown. */
const SHOW_COVER_PAGE = false;

/** Only mount the active book page and its immediate neighbors. */
const PAGE_PRELOAD_RADIUS = 1;

const isBookPagePreloaded = (bookIndex: number, activePage: number) =>
  Math.abs(bookIndex - activePage) <= PAGE_PRELOAD_RADIUS;

/**
 * Landing / 1st content page (DemoCard, SingleCard, SeasonCard, CollectionCard)
 * is kept in code but not shown — same approach as SHOW_COVER_PAGE.
 * Personal / collection cards prepend a Collection_Card shell via
 * resolveMainCardShellPages; that shell must stay hidden here or it becomes
 * an extra book page and TOC row #1.
 */
const SHOW_LANDING_PAGE = false;

/** Synthetic table-of-contents page inserted as the 2nd book page for all card types. */
const CARD_TOC_PAGE_TYPE = "CardToc";

const isCardTocPageType = (type?: string) =>
  String(type ?? "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase() === "cardtoc";

const normalizeNewsContent = (content: any): NewsItem[] => {
  if (Array.isArray(content)) {
    return content.map((item) => ({
      title: item?.title || "",
      description: item?.description || "",
      url: item?.url || "",
    }));
  }

  if (content?.title || content?.description || content?.url) {
    return [
      {
        title: content.title || "",
        description: content.description || "",
        url: content.url || "",
      },
    ];
  }

  return [];
};

interface CardPreviewModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional custom actions to render next to the close button (e.g. Unlock button). */
  headerRightSlot?: React.ReactNode;
  /** Optional page index to open on. 0 = first content page. */
  coverImageUrl?: string;
  template?: string;
  cardData?: any;
  pages: PreviewPage[];
  /** Extra pages to append to the modal (after normal processed pages). */
  additionalPages?: PreviewPage[];
  showCoverPage?: boolean;
  /** Collect / purchase this Card. Drives the Impact Card poster CTA. */
  onCollect?: () => void;
}

const CardPreviewModalContent: React.FC<CardPreviewModalProps> = ({
  open,
  cardData,
  onClose,
  headerRightSlot,
  coverImageUrl,
  template,
  pages = [],
  additionalPages,
  showCoverPage = true,
  onCollect,
}) => {
  const { darkMode } = useThemeStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { currentProfile } = useCurrentTalentProfile();
  const { userInfo, isTalentLogin, isUserLogin } = useAuth();
  const singleCardFanDisplayName = React.useMemo(
    () => getFanThankYouDisplayName(userInfo),
    [userInfo]
  );
  const isUserView = !currentProfile?.id;
  const { cubes } = useAnchorCubes();
  const isImpactCard =
    isImpactCardType(pages?.[0]?.type) ||
    isImpactCardType(cardData?.pages?.[0]?.type) ||
    isImpactCardType(cardData?.type);
  const cardTalentId = String(
    readImpactCardCreatorTalentId(cardData) ??
      readImpactCardCreatorTalentId(pages?.[0]) ??
      pages?.[0]?.content?.talent_id ??
      ""
  );
  const isImpactOwner = isImpactCardCreator(cardTalentId, currentProfile?.id);
  const isInvitedImpactPartner = React.useMemo(() => {
    const impactData = getImpactCardData({
      ...(typeof cardData?.content === "object" && !Array.isArray(cardData.content)
        ? cardData.content
        : {}),
      partner: cardData?.partner,
      partner_id: cardData?.partner_id,
    });
    return isInvitedImpactPartnerViewer(impactData.localPartner, [
      userInfo?.id,
      currentProfile?.id,
    ]);
  }, [cardData?.content, cardData?.partner, cardData?.partner_id, currentProfile?.id, userInfo?.id]);

  /** Has the viewer collected this Impact Card? */
  const isImpactCardCollected = React.useMemo(
    () => Boolean(cardData?.purchase != null || cardData?.pages?.[0]?.content?.is_purchased),
    [cardData?.purchase, cardData?.pages]
  );

  /** Contributor Status + Invite Partner: talent creator only. */
  const showImpactPeoplePage = Boolean(
    isTalentLogin &&
      !isUserLogin &&
      !isUserView &&
      isImpactOwner &&
      userInfo?.account_type !== USER_TYPES.USER
  );

  const hideCollectorOnlyPages = Boolean(isImpactOwner || isInvitedImpactPartner);

  const impactPurchaseId = React.useMemo(() => {
    const raw =
      (cardData?.purchase as { id?: unknown } | null | undefined)?.id ??
      cardData?.purchase_id ??
      cardData?.pages?.[0]?.content?.purchase_id;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }, [cardData?.purchase, cardData?.purchase_id, cardData?.pages]);

  const singleOrKIndieAudioPurchased = React.useMemo(() => {
    if (!isUserView) return true;
    const normalized = (template || "").trim().toLowerCase();
    if (normalized === "single card template") {
      return Boolean(cardData?.purchase != null || cardData?.pages?.[0]?.content?.is_purchased);
    }
    if (normalized === "k-indie card template") {
      return Boolean(cardData?.is_k_indie_card_purchased);
    }
    return true;
  }, [
    isUserView,
    template,
    cardData?.purchase,
    cardData?.is_k_indie_card_purchased,
    cardData?.pages,
  ]);

  const isSeasonCardBookPreview = Boolean(cardData?.isSeasonCardBookPreview);

  const isSeasonCardTalentView = Boolean(cardData?.seasonCardPreview?.onAddExperiencePage);

  const seasonCardPurchased = React.useMemo(() => {
    if (!isSeasonCardBookPreview) return true;
    if (isSeasonCardTalentView) return true;
    return Boolean(cardData?.purchase != null || cardData?.pages?.[0]?.content?.is_purchased);
  }, [isSeasonCardBookPreview, isSeasonCardTalentView, cardData?.purchase, cardData?.pages]);

  const [currentPage, setCurrentPage] = useState(0);
  const [impactScrollNonce, setImpactScrollNonce] = useState(0);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 390,
    h: typeof window !== "undefined" ? window.innerHeight : 844,
  }));

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    if (!open) return;
    const sync = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    sync();
    window.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("resize", sync);
    };
  }, [open]);

  const mainCardId = Number(cardData?.id ?? 0) || 0;

  const effectiveCardData = React.useMemo(() => {
    if (!isSeasonCardBookPreview) return cardData;
    return {
      ...cardData,
    };
  }, [cardData, isSeasonCardBookPreview]);

  const containerRef = useRef<HTMLDivElement>(null);
  const pagesTrackRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTimeRef = useRef(0);
  const swipeOffsetRef = useRef(0);
  const pageSwipeActiveRef = useRef(false);
  const isSwipingRef = useRef(false);
  const pageCommitRef = useRef(false);
  const currentPageRef = useRef(0);
  const isOpeningRef = useRef(false);
  const openedImpactStartRef = useRef(false);
  const pendingImpactScrollRef = useRef<{ columnId?: string; toTop?: boolean } | null>(null);

  const SWIPE_THRESHOLD = 50;
  const SWIPE_VELOCITY_THRESHOLD = 0.3;
  const PAGE_TRANSITION = "transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  const applyPagesTrackTransform = useCallback(
    (page: number, offsetPx: number, animate: boolean) => {
      const el = pagesTrackRef.current;
      if (!el) return;
      el.style.transition = animate ? PAGE_TRANSITION : "none";
      el.style.transform = `translate3d(calc(-${page * 100}% + ${offsetPx}px), 0, 0)`;
    },
    []
  );

  const getPageWidth = useCallback(
    () => containerRef.current?.clientWidth || window.innerWidth || 390,
    []
  );

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const pending = pendingImpactScrollRef.current;
    if (!pending || !isImpactCard) return;

    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      tries += 1;
      const done = pending.toTop
        ? scrollImpactBookToTop()
        : pending.columnId
          ? scrollImpactColumnIntoView(pending.columnId)
          : true;
      if (done) {
        pendingImpactScrollRef.current = null;
        return;
      }
      if (tries < 24) window.setTimeout(tick, 50);
    };
    const start = window.setTimeout(tick, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
    };
  }, [currentPage, impactScrollNonce, isImpactCard]);

  useEffect(() => {
    if (!open) return;

    if (isOpeningRef.current) return;

    if (isSwipingRef.current || pageSwipeActiveRef.current || pageCommitRef.current) {
      return;
    }

    applyPagesTrackTransform(currentPage, 0, true);
  }, [open, currentPage, applyPagesTrackTransform]);

  const { isSealed: isSealTimePassed } = useSealTimeGate(cardData?.seal_time);
  const hideSeasonExpCreation =
    isSealTimePassed || String(cardData?.card_status ?? "").toLowerCase() === "sealed";

  const processedPages: PreviewPage[] = React.useMemo(() => {
    const demoPurchased = cardData?.purchase != null;
    // Fans without purchase: hide episode pages + episode list.
    // Unreleased demo episodes belong in the listing only — drop their book pages.
    const withoutUnreleasedDemoExps = (list: PreviewPage[]) =>
      list.filter((page, index) => {
        const key = String(page.type ?? "")
          .replace(/[\s_-]+/g, "")
          .toLowerCase();
        // Standalone cover pages are not shown. Keep the last cover_image —
        // single / k-indie ending covers still render from that slot.
        if (!SHOW_COVER_PAGE && key === "coverimage") {
          const isLast = index === list.length - 1;
          if (!isLast) return false;
        }
        // Landing / 1st page (Demo / Single / Season / Collection / Youtube shell)
        // — same as cover. Collection personal cards otherwise keep an extra page.
        if (
          !SHOW_LANDING_PAGE &&
          (key === "democard" ||
            key === "singlecard" ||
            key === "seasoncard" ||
            key === "collectioncard" ||
            key === "youtubecard")
        ) {
          return false;
        }
        // Sealed season cards: drop creation pages so they don't count or show blank.
        if (key === "seasoncardexpcreation" || key === "seasoncardnormalexps") {
          if (hideSeasonExpCreation) return false;
        }

        if (key === "democardexps" || key === "democardexplist") {
          if (isUserView && !demoPurchased) return false;
        }
        if (key === "seasoncardexps" || key === "seasoncardexplist") {
          if (isUserView && !seasonCardPurchased) return false;
        }
        if (key === "democardexps") {
          // Talent/owner keeps unreleased pages; fans only see released.
          if (!isUserView) return true;
          return isDemoEpisodeReleased((page.content ?? {}) as Record<string, unknown>);
        }
        if (key === "seasoncardexps") {
          // Talent keeps Draft / Scheduled pages; fans only see Released.
          if (!isUserView) return true;
          return isSeasonSeriesReleased((page.content ?? {}) as Record<string, unknown>);
        }
        return true;
      });

    if (!template) return withoutUnreleasedDemoExps(pages);

    let result: PreviewPage[] = withoutUnreleasedDemoExps(pages);

    return result;
  }, [
    pages,
    template,
    t,
    cardData?.bmCard?.is_purchased,
    cardData?.seal_time,
    cardData?.purchase,
    isUserView,
    hideSeasonExpCreation,
  ]);

  const allPages: PreviewPage[] = React.useMemo(() => {
    const extras = (additionalPages || []).filter(Boolean);

    const rawBase = flattenImpactPages(
      extras.length === 0
        ? processedPages
        : [...processedPages, ...extras].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
    ) as PreviewPage[];

    const IMPACT_SECTION_TITLES: Record<ImpactCardBookSection, string> = {
      overview: "About the Impact",
      funding: "Goal",
      recipientPm: "PM Relief Fund Nepal",
      recipientAol: "AOL Nepal",
      contribute: "Contribute",
      contributeStatus: "Contribution Status",
      thanks: "Thank You",
      adminContributors: "Contributor Status",
      people: "Invite Local Partner",
    };

    const impactSections: Array<{
      section: ImpactCardBookSection | typeof IMPACT_POSTER_SECTION;
      pageTitle: string;
    }> = getImpactCardBookPageOrder({
      isCreator: showImpactPeoplePage,
      isPartner: hideCollectorOnlyPages && !showImpactPeoplePage,
    }).map((section) => ({
      section: section === "overview" ? IMPACT_POSTER_SECTION : section,
      pageTitle: IMPACT_SECTION_TITLES[section],
    }));

    const hasImpactShell = rawBase.some(
      (page) => isImpactCardType(page.type) && !isImpactAnchorPage(page) && !isImpactSubpage(page)
    );
    const shellContent =
      cardData?.content && typeof cardData.content === "object" && !Array.isArray(cardData.content)
        ? cardData.content
        : {};
    const withShell =
      isImpactCard && !hasImpactShell
        ? [
            {
              order: 0,
              type: IMPACT_CARD_TYPE,
              content: {
                ...shellContent,
                title: (shellContent as { title?: string }).title ?? cardData?.title,
                description:
                  (shellContent as { description?: string }).description ?? cardData?.description,
                talent: cardData?.talent,
                talent_id: cardData?.talent_id ?? cardData?.talent?.id,
              },
              assets: [] as number[],
            } satisfies PreviewPage,
            ...rawBase,
          ]
        : rawBase;

    const base = withShell.flatMap((page) => {
      if (isImpactSubpage(page)) return [];
      if (isImpactAnchorPage(page)) {
        const cubeId = String(page.content?.cube_id ?? page.content?.__impact_cube_id ?? "");
        return [
          {
            ...page,
            content: {
              ...(page.content ?? {}),
              __impact_cube_id: cubeId,
              __impact_stack: true,
            },
          },
        ];
      }
      if (!isImpactCardType(page.type)) return [page];

      return impactSections.map(({ section, pageTitle }, index) => ({
        ...page,
        order: Number(page.order ?? 0) + index / 10,
        content: {
          ...(page.content ?? {}),
          impact_card_title: page.content?.title,
          title: pageTitle,
          __impact_section: section,
          __impact_stack: true,
        },
      }));
    });

    const customCubes = cubes
      .filter((cube) => !cube.locked && !cubeSectionKey(cube))
      .filter((cube) => !base.some((page) => String(page.content?.cube_id || page.content?.__impact_cube_id) === cube.id))
      .map((cube, cubeIndex) => ({
        order: 800 + cubeIndex,
        type: "Impact_Card",
        content: {
          title: cube.title,
          description: cube.description,
          __impact_cube_id: cube.id,
          __impact_stack: true,
        },
        assets: [] as number[],
      }));

    const keepImpactPage = (page: PreviewPage) =>
      page?.content?.__impact_section !== "updates" &&
      page?.content?.__impact_section !== "overview" &&
      (!CREATOR_ONLY_SECTIONS.has(page?.content?.__impact_section) || showImpactPeoplePage) &&
      (!COLLECTOR_ONLY_SECTIONS.has(page?.content?.__impact_section) || !hideCollectorOnlyPages);

    if (base.some((page) => isCardTocPageType(page.type))) {
      return [...base, ...customCubes].filter(keepImpactPage);
    }

    const firstOrder = Number(base[0]?.order ?? 0);
    const secondOrder = Number(base[1]?.order ?? firstOrder + 1);

    const tocOrder = base.length === 0 ? 0 : firstOrder + (secondOrder - firstOrder) / 2;

    const tocPage: PreviewPage = {
      order: Number.isFinite(tocOrder) ? tocOrder : firstOrder + 0.5,
      type: CARD_TOC_PAGE_TYPE,
      content: {},
      assets: [],
    };

    // IMPORTANT:
    // TOC is the FIRST visible page.
    return [tocPage, ...base, ...customCubes].filter(keepImpactPage);
  }, [
    processedPages,
    additionalPages,
    cubes,
    isImpactCard,
    showImpactPeoplePage,
    hideCollectorOnlyPages,
    cardData,
  ]);

  const impactPageSource = React.useMemo(() => {
    const extras = (additionalPages || []).filter(Boolean);
    return extras.length === 0
      ? processedPages
      : [...processedPages, ...extras].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  }, [processedPages, additionalPages]);

  // Total pages: content pages only (cover / landing slots kept in code but not shown).
  const totalPages = (SHOW_COVER_PAGE ? 1 : 0) + allPages.length;

  const maxNavigablePage = totalPages - 1;

  /** Book index of the injected TOC page (after optional cover / landing). */
  // const tocBookPageIndex = (SHOW_COVER_PAGE ? 1 : 0) + (SHOW_LANDING_PAGE ? 1 : 0);
  const tocBookPageIndex = 0;

  const tocPages = React.useMemo(
    () =>
      buildCardTocPages({
        allPages,
        cardData,
        maxNavigablePage,
        includeCoverPage: false,
        t,
      }),
    [allPages, cardData, maxNavigablePage, showCoverPage, t]
  );

  const getSeasonPageIsPurchased = useCallback(
    (contentPageIndex: number) => {
      if (!isSeasonCardBookPreview) return singleOrKIndieAudioPurchased;
      if (isSeasonCardTalentView || seasonCardPurchased) return true;
      return true;
    },
    [
      allPages,
      isSeasonCardBookPreview,
      isSeasonCardTalentView,
      seasonCardPurchased,
      singleOrKIndieAudioPurchased,
    ]
  );

  useEffect(() => {
    if (!open) {
      isOpeningRef.current = false;
      openedImpactStartRef.current = false;

      currentPageRef.current = 0;
      setCurrentPage(0);

      swipeOffsetRef.current = 0;
      pageSwipeActiveRef.current = false;
      isSwipingRef.current = false;
      pageCommitRef.current = false;

      return;
    }

    // Collectors and partners open on TOC. Creators open on Contributor Status.
    isOpeningRef.current = true;

    currentPageRef.current = 0;
    setCurrentPage(0);

    swipeOffsetRef.current = 0;
    pageSwipeActiveRef.current = false;
    isSwipingRef.current = false;
    pageCommitRef.current = false;

    requestAnimationFrame(() => {
      if (!pagesTrackRef.current) return;
      const start = currentPageRef.current;
      applyPagesTrackTransform(start, 0, false);

      requestAnimationFrame(() => {
        isOpeningRef.current = false;
      });
    });
  }, [open, mainCardId, applyPagesTrackTransform]);

  useEffect(() => {
    if (!open || !isImpactCard || !showImpactPeoplePage || openedImpactStartRef.current) return;
    const index = allPages.findIndex(
      (page) => page?.content?.__impact_section === "adminContributors"
    );
    if (index < 0) return;
    openedImpactStartRef.current = true;
    currentPageRef.current = index;
    setCurrentPage(index);
    applyPagesTrackTransform(index, 0, false);
  }, [open, isImpactCard, showImpactPeoplePage, allPages, applyPagesTrackTransform]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // if (tocDrawerOpen || detailsGestures.isDrawerOpen || detailsGestures.isDragging) return;
      if ((e.target as HTMLElement).closest("button, a, [data-card-details-drawer]")) return;
      if (!e.touches[0]) return;
      const touch = e.touches[0];
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      startTimeRef.current = Date.now();
      swipeOffsetRef.current = 0;
      pageSwipeActiveRef.current = false;
      isSwipingRef.current = true;
      applyPagesTrackTransform(currentPageRef.current, 0, false);
    },
    [applyPagesTrackTransform]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isSwipingRef.current || !e.touches[0]) return;
      const touch = e.touches[0];
      const deltaX = touch.clientX - startXRef.current;
      const deltaY = touch.clientY - startYRef.current;

      if (!pageSwipeActiveRef.current) {
        if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return;
        if (Math.abs(deltaY) >= Math.abs(deltaX)) {
          isSwipingRef.current = false;
          return;
        }
        pageSwipeActiveRef.current = true;
      }

      e.preventDefault();
      const page = currentPageRef.current;
      const maxSwipe = getPageWidth() * 0.92;
      let clampedDelta = Math.max(-maxSwipe, Math.min(maxSwipe, deltaX));
      if (page === 0 && clampedDelta > 0) clampedDelta = 0;
      if (page >= maxNavigablePage && clampedDelta < 0) clampedDelta = 0;

      swipeOffsetRef.current = clampedDelta;
      // DOM write — avoids React re-render jank mid-drag
      applyPagesTrackTransform(page, clampedDelta, false);
    },
    [maxNavigablePage, applyPagesTrackTransform, getPageWidth]
  );

  const finishPageSwipe = useCallback(() => {
    const wasActive = isSwipingRef.current || pageSwipeActiveRef.current;
    const deltaX = swipeOffsetRef.current;
    const time = Date.now() - startTimeRef.current;
    const velocity = time > 0 ? Math.abs(deltaX) / time : 0;
    const page = currentPageRef.current;

    pageSwipeActiveRef.current = false;
    isSwipingRef.current = false;

    if (!wasActive) {
      swipeOffsetRef.current = 0;
      applyPagesTrackTransform(page, 0, true);
      return;
    }

    const shouldChangePage =
      Math.abs(deltaX) >= SWIPE_THRESHOLD ||
      (Math.abs(deltaX) >= 24 && velocity >= SWIPE_VELOCITY_THRESHOLD);

    let nextPage = page;
    if (shouldChangePage) {
      if (deltaX > 0 && page > 0) nextPage = page - 1;
      else if (deltaX < 0 && page < maxNavigablePage) nextPage = page + 1;
    }

    if (nextPage === page) {
      // Snap back smoothly
      swipeOffsetRef.current = 0;
      applyPagesTrackTransform(page, 0, true);
      return;
    }

    // Keep visual continuity: jump page index while compensating offset, then ease to 0.
    const width = getPageWidth();
    const compensated = deltaX + (deltaX > 0 ? -width : width);
    pageCommitRef.current = true;
    currentPageRef.current = nextPage;
    setCurrentPage(nextPage);
    swipeOffsetRef.current = compensated;
    applyPagesTrackTransform(nextPage, compensated, false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        swipeOffsetRef.current = 0;
        applyPagesTrackTransform(nextPage, 0, true);
        // Allow sync effect again after the settle animation starts.
        window.setTimeout(() => {
          pageCommitRef.current = false;
        }, 380);
      });
    });
  }, [maxNavigablePage, applyPagesTrackTransform, getPageWidth]);

  const handleTouchEnd = useCallback(() => {
    finishPageSwipe();
  }, [finishPageSwipe]);

  /** Desktop mouse / pen page drag (touch uses onTouch* to avoid double-handling). */
  const handlePagePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      if ((e.target as HTMLElement).closest("button, a, [data-card-details-drawer]")) return;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      startTimeRef.current = Date.now();
      swipeOffsetRef.current = 0;
      pageSwipeActiveRef.current = false;
      isSwipingRef.current = true;
      applyPagesTrackTransform(currentPageRef.current, 0, false);
    },
    [applyPagesTrackTransform]
  );

  const handlePagePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (!isSwipingRef.current) return;
      const deltaX = e.clientX - startXRef.current;
      const deltaY = e.clientY - startYRef.current;

      if (!pageSwipeActiveRef.current) {
        if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return;
        if (Math.abs(deltaY) >= Math.abs(deltaX)) {
          isSwipingRef.current = false;
          return;
        }
        pageSwipeActiveRef.current = true;
      }

      const page = currentPageRef.current;
      const maxSwipe = getPageWidth() * 0.92;
      let clampedDelta = Math.max(-maxSwipe, Math.min(maxSwipe, deltaX));
      if (page === 0 && clampedDelta > 0) clampedDelta = 0;
      if (page >= maxNavigablePage && clampedDelta < 0) clampedDelta = 0;

      swipeOffsetRef.current = clampedDelta;
      applyPagesTrackTransform(page, clampedDelta, false);
    },
    [maxNavigablePage, applyPagesTrackTransform, getPageWidth]
  );

  const handlePagePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      finishPageSwipe();
    },
    [finishPageSwipe]
  );

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 0) {
      const next = currentPage - 1;
      currentPageRef.current = next;
      setCurrentPage(next);
      applyPagesTrackTransform(next, 0, true);
    }
  }, [currentPage, applyPagesTrackTransform]);

  const goToNextPage = useCallback(() => {
    if (currentPage < maxNavigablePage) {
      const next = currentPage + 1;
      currentPageRef.current = next;
      setCurrentPage(next);
      applyPagesTrackTransform(next, 0, true);
    }
  }, [currentPage, maxNavigablePage, applyPagesTrackTransform]);

  const handleTocSelectPage = useCallback(
    (pageIndex: number) => {
      // Never allow child components to change the page
      // while the card is initially opening.
      if (isOpeningRef.current) return;

      if (pageIndex < 0 || pageIndex > maxNavigablePage) return;

      const fromPage = currentPageRef.current;
      const animate = Math.abs(pageIndex - fromPage) <= PAGE_PRELOAD_RADIUS;

      currentPageRef.current = pageIndex;
      setCurrentPage(pageIndex);

      swipeOffsetRef.current = 0;

      applyPagesTrackTransform(pageIndex, 0, animate);
    },
    [maxNavigablePage, applyPagesTrackTransform]
  );

  // ── Add this effect ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goToPreviousPage();
      } else if (e.key === "ArrowRight") {
        goToNextPage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [goToPreviousPage, goToNextPage]);

  const normalizeTemplate = (val?: string) => (val || "").trim().toLowerCase();
  const isSingleCardTemplate = normalizeTemplate(template) === "single card template";
  const isKIndieCardTemplate = normalizeTemplate(template) === "k-indie card template";
  const isSingleOrKIndieSealed =
    (isSingleCardTemplate || isKIndieCardTemplate) && isCardSealed(cardData?.seal_time);

  const isListeningTogetherCardTemplate =
    normalizeTemplate(template) === "listening together card template";
  const isDemoCardTemplate = normalizeTemplate(template) === "demo card template";
  const isSeasonCardTemplate = normalizeTemplate(template) === "season card template";

  const isStaiExperiencePage = (pageType?: string) => {
    const normalized = (pageType || "").replace(/_/g, "").toLowerCase();
    return (
      normalized === "text" ||
      normalized === "audio" ||
      normalized === "video" ||
      normalized === "image" ||
      normalized === "imagegrid" ||
      normalized === "youtube"
    );
  };

  const cardExpInteractionPurchased = React.useMemo(() => {
    if (!isUserView) return true;
    if (isSeasonCardBookPreview) return seasonCardPurchased;
    if (isSingleCardTemplate || isKIndieCardTemplate) {
      return singleOrKIndieAudioPurchased;
    }
    if (isDemoCardTemplate) {
      return Boolean(cardData?.purchase != null || cardData?.pages?.[0]?.content?.is_purchased);
    }
    if (isSeasonCardTemplate) {
      return Boolean(cardData?.purchase != null || cardData?.pages?.[0]?.content?.is_purchased);
    }
    if (isListeningTogetherCardTemplate) {
      return Boolean(
        cardData?.is_listening_together_card_purchased ??
        cardData?.pages?.[0]?.content?.is_purchased
      );
    }
    return true;
  }, [
    isUserView,
    isSingleCardTemplate,
    isKIndieCardTemplate,
    isDemoCardTemplate,
    isSeasonCardTemplate,
    isSeasonCardBookPreview,
    seasonCardPurchased,
    singleOrKIndieAudioPurchased,
    cardData?.is_listening_together_card_purchased,
    cardData?.purchase,
    cardData?.pages,
  ]);

  const cardExpInteractionVariant = React.useMemo(() => {
    if (isSingleCardTemplate || isKIndieCardTemplate) return "single" as const;
    if (isDemoCardTemplate) return "demo" as const;
    if (isSeasonCardTemplate) return "season" as const;
    return null;
  }, [isSingleCardTemplate, isKIndieCardTemplate, isDemoCardTemplate, isSeasonCardTemplate]);

  const isSingleCardSealed = isSingleCardTemplate && isCardSealed(cardData?.seal_time);

  const canInteractOnExpPages = Boolean(cardExpInteractionVariant) && cardExpInteractionPurchased;
  // && !isSingleCardSealed;

  const interactionTalentId = Number(cardData?.talent?.id ?? 0) || undefined;
  const interactionTalentName = cardData?.talent?.username;
  const interactionTalentAvatar = cardData?.talent?.profile_image;

  // Map type to talent experience key
  // For Image type, we need to infer if it's image_grid or image based on content
  const typeToTalentExperienceKey = (type: string, content: any): string | null => {
    if (type === "Image") {
      // Infer from content: if url array has more than 1 item, it's image_grid
      const urlArray = Array.isArray(content?.url) ? content.url : [];
      return urlArray.length > 1 ? "image_grid" : "image";
    }

    // Normalize type for flexible backend/client inputs:
    // "Flash_Card", "flash_card", "FlashCard" -> "flashcard"
    const normalizedType = (type || "").replace(/[\s_-]/g, "").toLowerCase();

    const typeMap: Record<string, string> = {
      audio: "audio",
      video: "video",
      text: "text",
      image: "image",
      songs: "songs",
      singlecard: "single_card",
      seasoncard: "season_card",
      kindiecard: "k_indie_card",
      postcard: "post_card",
      cover: "cover_image",
      coverimage: "cover_image",
      youtube: "youtube",
      collectioncard: "collection_card",
      // API page type is `youtube_card_content` → normalized `youtubecardcontent`
      youtubecardcontent: "youtube_card_content",
      collectioncardcontent: "collection_card_content",
      news: "news",
      about: "about",
      bonus: "bonus",
      instrument: "instrument",
      democard: "demo_card",
      democardexps: "demo_card_exps",
      democardexplist: "demo_card_exp_list",
      democardexpcreation: "demo_card_exp_creation",
      democardnormalexps: "demo_card_normal_exps",
      singlecardexpcreation: "single_card_exp_creation",
      seasoncardexps: "season_card_exps",
      seasoncardexplist: "season_card_exp_list",
      seasoncardexpcreation: "season_card_exp_creation",
      seasoncardnormalexps: "season_card_normal_exps",
    };

    return typeMap[normalizedType] || null;
  };

  // Convert page content to TalentExperienceData format
  const contentToTalentExperienceData = (page: PreviewPage): TalentExperienceData => {
    const content = page.content || {};

    const key = typeToTalentExperienceKey(page.type, content);
    if (!key) return {};

    switch (key) {
      case "cover_image":
        return {
          coverImage: {
            imageUrl:
              content.imageUrl || (Array.isArray(content.url) ? content.url[0] : content.url) || "",
          },
        };
      case "image_grid":
        return {
          imageGrid: {
            images: Array.isArray(content.url) ? content.url : [],
            description: content.description || "",
          },
        };
      case "image":
        return {
          image: {
            imageUrl: Array.isArray(content.url) ? content.url[0] : content.url,
            description: content.description || "",
          },
        };
      case "text":
        return {
          text: {
            title: content.title || "",
            description: content.description || "",
          },
        };

      case "audio":
        return {
          audio: {
            audioUrl: content.url || "",
            description: content.description || "",
          },
        };
      case "video":
        return {
          video: {
            videoUrl: content.url || "",
            description: content.description || "",
          },
        };

      case "k_indie_card":
        return {
          kIndieCard: content,
        };
      case "single_card":
        return {
          singleCard: content,
        };

      case "season_card":
        return {
          seasonCard: content,
        };

      case "youtube":
        return {
          youtube: {
            title: content.title || "",
            description: content.description || "",
            url: content.url || "",
          },
        };

      case "collection_card":
        return {
          collectionCard: content,
        };

      case "youtube_card_content":
        return {
          youtube: content,
        };

      case "about":
        return {
          about: {
            title: content.title || "",
            description: content.description || "",
            descriptionImage: content.description_image || content.descriptionImage || "",
            extraDescription: content.extra_description || content.extraDescription || "",
            extraDescriptionImage:
              content.extra_description_image || content.extraDescriptionImage || "",
            moodNote: content.mood_note || content.moodNote || "",
            momentImage: content.moment_image || content.momentImage || "",
            moments: Array.isArray(content.moments) ? content.moments : [],
            credits: Array.isArray(content.credits) ? content.credits : [],
          },
        };
      case "bonus":
        return {
          bonus: {
            title: content.title || "",
            description: content.description || "",
            voiceNote: content.voice_note || content.voiceNote || "",
            image: content.image || "",
            demoSnippet: content.demo_snippet || content.demoSnippet || "",
            cardLinks: Array.isArray(content.card_links || content.cardLinks)
              ? content.card_links || content.cardLinks
              : [],
          },
        };
      case "instrument":
        return {
          instrument: {
            title: content.title || "",
            url: content.url || "",
            artistName: content.artist_name || content.artistName || "",
            description: content.description || "",
          },
        };

      case "news":
        return {
          news: normalizeNewsContent(content),
        };

      case "post_card":
        return {
          postCard: content,
        };

      case "demo_card":
        return {
          demoCard: content,
        };

      case "listening_together_card":
        return {
          listeningTogetherCard: content,
        };

      case "listening_together_exp_creation":
        return {
          listeningTogetherExpCreation: content,
        };

      case "listening_together_exp":
        return {
          listeningTogetherExp: content,
        };

      case "demo_card_exps":
        return {
          demoCardExps: content,
        };

      case "demo_card_exp_list":
        return {
          demoCardExpList: content,
        };

      case "demo_card_exp_creation":
        return {
          demoCardExpCreation: content,
        };

      case "demo_card_normal_exps":
        return {
          demoCardExpCreation: content,
        };

      case "link_card_exp_creation":
        return {
          linkCardExpCreation: content,
        };

      case "link_card_exp_list":
        return {
          linkCardExpList: content,
        };

      case "single_card_exp_creation":
        return {
          singleCardExp: content,
        };

      case "season_card_exps":
        return {
          seasonCardExps: content,
        };

      case "season_card_exp_list":
        return {
          seasonCardExpList: content,
        };

      case "season_card_exp_creation":
        return {
          seasonCardExpCreation: content,
        };

      case "season_card_normal_exps":
        return {
          seasonCardExpCreation: content,
        };

      default:
        return {};
    }
  };

  // Previous sizing used a fixed height instead of aspect-ratio:
  //   width: "100%",
  //   maxWidth: { xs: "100%", sm: "500px", md: "550px" },
  //   height: isMobile ? "70dvh" : 750, // cover
  //   height: isMobile ? "100%" : 750,  // content pages
  const cardPageLayoutSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
  } as const;

  // Cover (and desktop pages): 2/3 portrait like DeckCardUnifiedFace.
  const coverPageFrameSx = {
    ...cardPageLayoutSx,
    width: `min(100%, calc((100dvh - 120px) * ${CARD_ASPECT_RATIO}))`,
    aspectRatio: CARD_ASPECT_RATIO_CSS,
    height: "auto",
    maxHeight: "100%",
  };

  // Content pages: full screen on mobile, 2/3 on desktop.
  const cardPageFrameSx = isMobile
    ? {
        ...cardPageLayoutSx,
        width: "100%",
        maxWidth: "100%",
        height: "100%",
      }
    : coverPageFrameSx;

  const impactStackFrameSx = {
    ...cardPageFrameSx,
    height: "auto",
    minHeight: "100%",
    maxHeight: "none",
    overflow: "visible",
    alignItems: "center",
    justifyContent: "flex-start",
  };

  // Render cover page (page 0)
  const renderCoverPage = () => {
    // For Single Card template, find the single_card page and merge its data
    // so CoverImageDisplay can render the album overlay (title, description, price)
    let singleCardData: TalentExperienceData["singleCard"] | undefined;
    if (isSingleCardTemplate) {
      const singleCardPage = pages.find((p) => {
        const key = typeToTalentExperienceKey(p.type, p.content);
        return key === "single_card";
      });
      if (singleCardPage) {
        singleCardData = {
          ...singleCardPage.content,
          price: cardData?.price ?? singleCardPage.content?.price,
        };
      } else if (
        cardData?.content &&
        typeof cardData.content === "object" &&
        !Array.isArray(cardData.content)
      ) {
        // New main-card shape: shell lives on top-level content
        singleCardData = {
          ...cardData.content,
          price: cardData?.price ?? cardData.content?.price,
        };
      }
    }

    let kIndieCardData: TalentExperienceData["kIndieCard"] | undefined;
    if (isKIndieCardTemplate) {
      const kIndieCardPage = pages.find((p) => {
        const key = typeToTalentExperienceKey(p.type, p.content);
        return key === "k_indie_card";
      });
      if (kIndieCardPage) {
        kIndieCardData = kIndieCardPage.content;
      }
    }

    let audioData: TalentExperienceData["audio"] | undefined;
    if (isSingleCardTemplate || isKIndieCardTemplate) {
      const audioPage = pages.find((p) => {
        const key = typeToTalentExperienceKey(p.type, p.content);
        return key === "audio";
      });
      if (audioPage) {
        audioData = audioPage.content;
      }
    }

    let listeningTogetherData: TalentExperienceData["listeningTogetherCard"] | undefined;
    if (isListeningTogetherCardTemplate) {
      const listeningTogetherPage = pages.find((p) => {
        const key = typeToTalentExperienceKey(p.type, p.content);
        return key === "listening_together_card";
      });
      if (listeningTogetherPage) {
        const pageListeningTogether = listeningTogetherPage.content;
        listeningTogetherData = {
          ...pageListeningTogether,
          talent: cardData?.talent ?? undefined,
        };
      }
    }

    let demoCardData: TalentExperienceData["demoCard"] | undefined;
    if (isDemoCardTemplate) {
      const demoCardPage = pages.find((p) => {
        const key = typeToTalentExperienceKey(p.type, p.content);
        return key === "demo_card";
      });
      if (demoCardPage) {
        const pageDemo = demoCardPage.content;
        demoCardData = {
          ...pageDemo,
          price: cardData?.price ?? pageDemo?.price,
          talent: cardData?.talent ?? undefined,
        };
      } else if (
        cardData?.content &&
        typeof cardData.content === "object" &&
        !Array.isArray(cardData.content)
      ) {
        demoCardData = {
          ...cardData.content,
          price: cardData?.price ?? cardData.content?.price,
          talent: cardData?.talent ?? undefined,
        };
      }
    }

    let seasonCardData: TalentExperienceData["seasonCard"] | undefined;
    if (isSeasonCardTemplate) {
      const seasonCardPage = pages.find((p) => {
        const key = typeToTalentExperienceKey(p.type, p.content);
        return key === "season_card";
      });
      if (seasonCardPage) {
        seasonCardData = {
          ...seasonCardPage.content,
          price: cardData?.price ?? seasonCardPage.content?.price,
          talent: cardData?.talent ?? undefined,
        };
      } else if (
        cardData?.content &&
        typeof cardData.content === "object" &&
        !Array.isArray(cardData.content)
      ) {
        seasonCardData = {
          ...cardData.content,
          price: cardData?.price ?? cardData.content?.price,
          talent: cardData?.talent ?? undefined,
        };
      }
    }

    const coverImageData: TalentExperienceData = {
      coverImage: {
        imageUrl: coverImageUrl || "",
      },
      ...(singleCardData
        ? {
            singleCard: singleCardData,
            audio: audioData,
            seal_time: cardData?.seal_time ?? null,
          }
        : {}),
      ...(kIndieCardData ? { kIndieCard: kIndieCardData, audio: audioData } : {}),
      ...(listeningTogetherData ? { listeningTogetherCard: listeningTogetherData } : {}),
      ...(demoCardData ? { demoCard: demoCardData } : {}),
      ...(seasonCardData ? { seasonCard: seasonCardData } : {}),
      ...(cardData?.talent ? { talent: cardData.talent } : {}),
    };

    const coverImageValue = t("cover_image", { ns: "taascard" });
    const seasonFrame = isSeasonCardTemplate;
    return (
      <Box sx={coverPageFrameSx}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
            borderRadius: "16px",
            border: seasonFrame
              ? darkMode
                ? "2px solid rgba(105,171,124,0.35)"
                : "2px solid rgba(54,97,66,0.28)"
              : darkMode
                ? "2px solid var(--ws-primary-alpha-20)"
                : "2px solid var(--primary-200)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: seasonFrame
              ? darkMode
                ? "var(--shadow-lg), 0 0 0 2px rgba(105,171,124,0.12), 0 0 28px rgba(105,171,124,0.18)"
                : "var(--shadow-lg), 0 0 0 2px rgba(105,171,124,0.14), 0 0 28px rgba(54,97,66,0.12)"
              : darkMode
                ? "var(--shadow-lg), 0 0 0 2px var(--ws-primary-alpha-10), 0 0 30px color-mix(in srgb, var(--ws-purple-500) 20%, transparent)"
                : "var(--shadow-lg), 0 0 0 2px var(--primary-100), 0 0 30px var(--ws-purple-200)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <LazyTalentExperienceDisplayReadOnly
            canInteract={canInteractOnExpPages}
            interactionVariant={cardExpInteractionVariant ?? undefined}
            interactionTalentId={interactionTalentId}
            interactionTalentName={interactionTalentName}
            interactionTalentAvatar={interactionTalentAvatar}
            talentExperience={coverImageValue}
            data={coverImageData}
            template={template}
            pages={pages}
            onClosePreviewModal={onClose}
          />
        </Box>
      </Box>
    );
  };

  // Render content page
  const renderContentPage = (page: PreviewPage, index: number) => {
    const content = page.content || {};
    const cubeId = String(content?.__impact_cube_id || content?.cube_id || "");

    const pageIsPurchased = getSeasonPageIsPurchased(index);

    if (cubeId && !content?.__impact_section) {
      const cube =
        cubes.find((item) => item.id === cubeId) ||
        cubes.find((item) => item.pageId != null && item.pageId === Number(page.id));
      const nested = cubeFromSubpages(Number(page.id) || cube?.pageId, impactPageSource);
      const mergedCube = nested
        ? {
            ...(cube ?? nested),
            columns: uniqueColumns([...(cube?.columns ?? []), ...nested.columns]),
          }
        : cube;
      return (
        <Box sx={impactStackFrameSx}>
          <ImpactCubePage cube={mergedCube} />
        </Box>
      );
    }

    if (isImpactCardType(page.type)) {
      const impactCardData = getImpactCardData({
        ...(typeof cardData?.content === "object" && !Array.isArray(cardData.content)
          ? cardData.content
          : {}),
        ...content,
        partner: cardData?.partner ?? content?.partner,
        partner_id: cardData?.partner_id ?? content?.partner_id,
        collector_count:
          cardData?.collector_count ??
          (typeof cardData?.content === "object" && !Array.isArray(cardData.content)
            ? (cardData.content as { collector_count?: number }).collector_count
            : undefined) ??
          content?.collector_count,
        clicked_count:
          cardData?.clicked_count ??
          (typeof cardData?.content === "object" && !Array.isArray(cardData.content)
            ? (cardData.content as { clicked_count?: number }).clicked_count
            : undefined) ??
          content?.clicked_count,
        viewed_count:
          cardData?.viewed_count ??
          (typeof cardData?.content === "object" && !Array.isArray(cardData.content)
            ? (cardData.content as { viewed_count?: number }).viewed_count
            : undefined) ??
          content?.viewed_count,
      });
      const talent = content?.talent ?? cardData?.talent;
      const talentId =
        readImpactCardCreatorTalentId(cardData) ??
        readImpactCardCreatorTalentId({
          talent,
          talent_id: content?.talent_id ?? cardData?.talent_id,
        }) ??
        (cardTalentId || undefined);
      const isOwner = isImpactOwner;

      if (CREATOR_ONLY_SECTIONS.has(content?.__impact_section) && !showImpactPeoplePage) {
        return null;
      }
      if (COLLECTOR_ONLY_SECTIONS.has(content?.__impact_section) && hideCollectorOnlyPages) {
        return null;
      }
      const section = (
        content?.__impact_section === IMPACT_POSTER_SECTION
          ? "overview"
          : content?.__impact_section ?? "overview"
      ) as ImpactCardBookSection;
      const cube = findCubeForSection(
        cubes,
        content?.__impact_section === IMPACT_POSTER_SECTION ? IMPACT_POSTER_SECTION : section
      );

      const bookScrollActive =
        currentPage === (SHOW_COVER_PAGE ? 1 : 0) + index;

      const bookPage = (
        <ImpactCardBookPage
          section={section}
          data={impactCardData}
          title={content?.impact_card_title ?? cardData?.title ?? "Impact Card"}
          description={content?.description ?? cardData?.description}
          talentName={talent?.view_name ?? talent?.username}
          isLoggedIn={Boolean(userInfo?.id)}
          isOwner={isOwner}
          isCollected={isImpactCardCollected}
          onSignIn={() => setLoginDialogOpen(true)}
          onNavigate={(sectionKey) => {
            pendingImpactScrollRef.current = { toTop: true };
            setImpactScrollNonce((value) => value + 1);
            const nextIndex = allPages.findIndex(
              (item) => item?.content?.__impact_section === sectionKey
            );
            if (nextIndex >= 0) handleTocSelectPage(nextIndex);
          }}
          mainCardId={Number(cardData?.id) || undefined}
          cardTalentId={talentId}
          stackWithParent
          bookScrollActive={bookScrollActive}
          purchaseId={impactPurchaseId}
          coverImageUrl={cardData?.cover_image_url ?? coverImageUrl}
          cardContent={
            typeof cardData?.content === "object" && !Array.isArray(cardData.content)
              ? cardData.content
              : content
          }
        />
      );

      return (
        <Box sx={impactStackFrameSx}>
          {section === "thanks" ? (
            bookPage
          ) : (
            <ImpactStackedPage
              cube={
                section === "adminContributors" ? undefined : cube
              }
              fit={
                section === "adminContributors" ? "content" : "page"
              }
            >
              {bookPage}
            </ImpactStackedPage>
          )}
        </Box>
      );
    }

    if (isCardTocPageType(page.type)) {
      return (
        <CardTocPage
          frameSx={cardPageFrameSx}
          isMobile={isMobile}
          cardTitle={String(cardData?.talent?.username || "")}
          cardDescription={String(cardData?.description || cardData?.content?.description || "")}
          pageCount={totalPages}
          pages={tocPages}
          currentPage={currentPage}
          onSelectPage={handleTocSelectPage}
          talentCubeToc={isImpactCard}
          canEditCubes={
            isImpactCard &&
            ((isImpactOwner && isTalentLogin && !isUserLogin && !isUserView) ||
              isInvitedImpactPartner)
          }
          partnerEditOnly={isImpactCard && isInvitedImpactPartner && !isImpactOwner}
          coverImageUrl={cardData?.cover_image_url ?? coverImageUrl}
          onSelectImpactSection={(sectionKey: string) => {
            pendingImpactScrollRef.current = { toTop: true };
            setImpactScrollNonce((value) => value + 1);
            const index = allPages.findIndex(
              (item) => item?.content?.__impact_section === sectionKey
            );
            if (index >= 0) handleTocSelectPage(index);
          }}
          onSelectImpactCube={(cubeId: string) => {
            pendingImpactScrollRef.current = { toTop: true };
            setImpactScrollNonce((value) => value + 1);
            const cube = cubes.find((item) => item.id === String(cubeId));
            const section = cube ? cubeSectionKey(cube) : undefined;
            const sectionIndex =
              section != null
                ? allPages.findIndex((item) => item?.content?.__impact_section === section)
                : -1;
            const cubePageIndex = allPages.findIndex(
              (item) => String(item?.content?.__impact_cube_id || item?.content?.cube_id || "") === String(cubeId)
            );
            const nextIndex = sectionIndex >= 0 ? sectionIndex : cubePageIndex;
            if (nextIndex >= 0) handleTocSelectPage(nextIndex);
          }}
          onSelectImpactColumn={(columnId: string, cubeId?: string, sectionKey?: string) => {
            pendingImpactScrollRef.current = { columnId };
            setImpactScrollNonce((value) => value + 1);
            const cube =
              (cubeId ? cubes.find((item) => item.id === String(cubeId)) : undefined) ||
              cubes.find((item) => item.columns.some((column) => column.id === columnId));
            const cubeSection = cube ? cubeSectionKey(cube) : sectionKey;
            const cubePageIndex = cube
              ? allPages.findIndex(
                  (item) => String(item?.content?.__impact_cube_id || item?.content?.cube_id || "") === cube.id
                )
              : -1;
            const sectionIndex = allPages.findIndex(
              (item) => item?.content?.__impact_section === cubeSection
            );
            const nextIndex = cubePageIndex >= 0 ? cubePageIndex : sectionIndex;
            if (nextIndex >= 0) handleTocSelectPage(nextIndex);
          }}
        />
      );
    }

    // Synthetic "sealed" page injected in book view mode (published cards)
    if (page.type === "SealedThankYou") {
      const message: string = "Thanks for joining me, {username}.";
      const signatureUrl: string | undefined = content?.signature;

      return (
        <Box sx={cardPageFrameSx}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
              borderRadius: isMobile ? 0 : "16px",
              border: isMobile
                ? "none"
                : darkMode
                  ? "2px solid var(--ws-primary-alpha-20)"
                  : "2px solid var(--primary-200)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isMobile
                ? "none"
                : darkMode
                  ? "var(--shadow-lg), 0 0 0 2px var(--ws-primary-alpha-10), 0 0 30px color-mix(in srgb, var(--ws-purple-500) 20%, transparent)"
                  : "var(--shadow-lg), 0 0 0 2px var(--primary-100), 0 0 30px var(--ws-purple-200)",
              position: "relative",
              overflow: "hidden",
              px: { xs: 2.5, sm: 4 },
              py: { xs: 3, sm: 4 },
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Typography
              sx={{
                color: darkMode ? "var(--neutralWhite)" : "var(--neutral-900)",
                fontFamily: '"Lexend", sans-serif',
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                textAlign: "center",
                lineHeight: 1.4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {message}
            </Typography>

            {signatureUrl ? (
              <Box
                component="img"
                src={signatureUrl}
                alt="Signature"
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  maxHeight: 140,
                  objectFit: "contain",
                  borderRadius: 2,
                  opacity: 0.95,
                  filter: darkMode ? "brightness(1.05) contrast(1.05)" : "none",
                }}
              />
            ) : null}
          </Box>
        </Box>
      );
    }

    //for single card and k-indie card ending page only
    const normalizedSlugForEnding = (page.type || "").replace(/_/g, "").toLowerCase();
    if (
      (isSingleCardTemplate || isKIndieCardTemplate) &&
      normalizedSlugForEnding === "coverimage" &&
      index === allPages.length - 1
    ) {
      const imageUrl =
        content.imageUrl || (Array.isArray(content.url) ? content.url[0] : content.url) || "";
      const endingClosingNote = isSingleOrKIndieSealed
        ? {
            fanDisplayName: singleCardFanDisplayName,
            fanAvatarUrl: userInfo?.profile_image ?? null,
            signatureImageUrl: cardData?.talent?.username,
          }
        : undefined;
      return (
        <Box sx={cardPageFrameSx}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
              borderRadius: isMobile ? 0 : "16px",
              border: isMobile
                ? "none"
                : darkMode
                  ? "2px solid var(--ws-primary-alpha-20)"
                  : "2px solid var(--primary-200)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isMobile
                ? "none"
                : darkMode
                  ? "var(--shadow-lg), 0 0 0 2px var(--ws-primary-alpha-10), 0 0 30px color-mix(in srgb, var(--ws-purple-500) 20%, transparent)"
                  : "var(--shadow-lg), 0 0 0 2px var(--primary-100), 0 0 30px var(--ws-purple-200)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardPageContent
              page={{
                type: "Image",
                content: { url: imageUrl, description: "", title: "" },
              }}
              isFromBookView
              talent={content?.talent ?? cardData?.talent}
              template={template}
              closingNote={endingClosingNote}
              isPurchased={pageIsPurchased}
              pageId={page?.id != null ? Number(page.id) : undefined}
              canInteract={canInteractOnExpPages}
              interactionVariant={cardExpInteractionVariant ?? undefined}
              interactionTalentId={interactionTalentId}
              interactionTalentName={interactionTalentName}
              interactionTalentAvatar={interactionTalentAvatar}
              isSingleCardSealed={isSingleCardSealed}
              isPageActive={currentPage === index + (SHOW_COVER_PAGE ? 1 : 0)}
            />
          </Box>
        </Box>
      );
    }

    // Text / Audio / Video / Image / ImageGrid — book-view experience pages
    if (isStaiExperiencePage(page.type)) {
      const normalizedTypeForClosing = (page.type || "").replace(/_/g, "").toLowerCase();
      const isImageLikeBookPage =
        normalizedTypeForClosing === "image" || normalizedTypeForClosing === "imagegrid";
      const lastImageClosingNote =
        isSingleOrKIndieSealed && isImageLikeBookPage && index === allPages.length - 1
          ? {
              fanDisplayName: singleCardFanDisplayName,
              fanAvatarUrl: userInfo?.profile_image ?? null,
              signatureImageUrl: cardData?.talent?.username,
            }
          : undefined;

      const nestedSubpages = isImpactCard
        ? cubeFromSubpages(Number(page.id), impactPageSource)
        : undefined;

      const experiencePage = (
        <Box sx={cardPageFrameSx}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
              borderRadius: isMobile ? 0 : "16px",
              border: isMobile
                ? "none"
                : darkMode
                  ? "2px solid var(--ws-primary-alpha-20)"
                  : "2px solid var(--primary-200)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isMobile
                ? "none"
                : darkMode
                  ? "var(--shadow-lg), 0 0 0 2px var(--ws-primary-alpha-10), 0 0 30px color-mix(in srgb, var(--ws-purple-500) 20%, transparent)"
                  : "var(--shadow-lg), 0 0 0 2px var(--primary-100), 0 0 30px var(--ws-purple-200)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardPageContent
              page={{
                type: page.type,
                content: page.content,
                created_at: page.created_at || cardData?.createdAt,
                cover_image_url:
                  content?.cover_image_url ||
                  content?.coverImageUrl ||
                  cardData?.cover_image_url ||
                  cardData?.coverImageUrl ||
                  coverImageUrl ||
                  "",
              }}
              isFromBookView
              talent={content?.talent ?? cardData?.talent}
              template={template}
              closingNote={lastImageClosingNote}
              isPurchased={pageIsPurchased}
              pageId={page?.id != null ? Number(page.id) : undefined}
              canInteract={canInteractOnExpPages}
              interactionVariant={cardExpInteractionVariant ?? undefined}
              interactionTalentId={interactionTalentId}
              interactionTalentName={interactionTalentName}
              interactionTalentAvatar={interactionTalentAvatar}
              isSingleCardSealed={isSingleCardSealed}
              isPageActive={currentPage === index + (SHOW_COVER_PAGE ? 1 : 0)}
            />
          </Box>
        </Box>
      );

      if (!nestedSubpages) return experiencePage;

      return (
        <Box sx={impactStackFrameSx}>
          <ImpactStackedPage cube={nestedSubpages}>{experiencePage}</ImpactStackedPage>
        </Box>
      );
    }

    const talentExperienceKey = typeToTalentExperienceKey(page.type, content);
    if (!talentExperienceKey) {
      return (
        <Box
          sx={{
            ...cardPageFrameSx,
            color: darkMode ? "var(--neutral-300)" : "var(--neutral-600)",
            px: { xs: 1, sm: 2 },
          }}
        >
          <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            Unknown page type: {page.type}
          </Typography>
        </Box>
      );
    }

    const talentExperienceValue = t(talentExperienceKey, { ns: "taascard" });
    const experienceData = contentToTalentExperienceData(page);

    return (
      <Box sx={cardPageFrameSx}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
            borderRadius: isMobile ? 0 : "16px",
            border: isMobile
              ? "none"
              : darkMode
                ? "2px solid var(--ws-primary-alpha-20)"
                : "2px solid var(--primary-200)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: isMobile
              ? "none"
              : darkMode
                ? "var(--shadow-lg), 0 0 0 2px var(--ws-primary-alpha-10), 0 0 30px color-mix(in srgb, var(--ws-purple-500) 20%, transparent)"
                : "var(--shadow-lg), 0 0 0 2px var(--primary-100), 0 0 30px var(--ws-purple-200)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <LazyTalentExperienceDisplayReadOnly
            canInteract={canInteractOnExpPages}
            interactionVariant={cardExpInteractionVariant ?? undefined}
            interactionTalentId={interactionTalentId}
            interactionTalentName={interactionTalentName}
            interactionTalentAvatar={interactionTalentAvatar}
            talentExperience={talentExperienceValue}
            data={experienceData}
            template={template}
            pages={pages}
            pageOrder={page.order}
            cardData={effectiveCardData}
            pageId={page?.id}
            onClosePreviewModal={onClose}
            page={page}
            isPageActive={currentPage === index + (SHOW_COVER_PAGE ? 1 : 0)}
            onPageSelection={handleTocSelectPage}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 300,
          sx: {
            backgroundColor: darkMode ? "rgba(0, 0, 0, 0.85)" : "rgba(0, 0, 0, 0.75)",
          },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
          "& .MuiBackdrop-root": {
            backgroundColor: darkMode ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.75)",
          },
        }}
      >
        <Fade in={open}>
          <Box
            ref={(node: HTMLDivElement | null) => {
              (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={handlePagePointerDown}
            onPointerMove={handlePagePointerMove}
            onPointerUp={handlePagePointerUp}
            onPointerCancel={handlePagePointerUp}
            sx={{
              width: "100%",
              height: "100dvh",
              maxHeight: "100dvh",
              position: "relative",
              background: darkMode
                ? "radial-gradient(ellipse at center, rgba(139,92,246,0.1) -90%, rgba(8,14,24,0.95) 90%)"
                : "radial-gradient(ellipse at center, rgba(139,92,246,0.05) -90%, rgba(245,247,251,0.98) 90%)",
              overflow: "hidden",
              touchAction:
                // detailsGestures.isDrawerOpen || detailsGestures.isDragging ? "none" : "pan-y",
                "none",
              display: "flex",
              flexDirection: "column",
              WebkitOverflowScrolling: "touch",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: darkMode
                  ? "radial-gradient(circle at 20% 30%, rgba(139,92,246,0.08) 0%, transparent 50%)"
                  : "radial-gradient(circle at 20% 30%, rgba(139,92,246,0.03) 0%, transparent 50%)",
                pointerEvents: "none",
                zIndex: 1,
              },
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Close Button + optional header actions (e.g. Unlock Access) */}
            <Box
              sx={{
                position: "absolute",
                top: {
                  xs: "max(12px, env(safe-area-inset-top, 12px))",
                  sm: "max(16px, env(safe-area-inset-top, 16px))",
                },
                right: {
                  xs: "max(12px, env(safe-area-inset-right, 12px))",
                  sm: "max(16px, env(safe-area-inset-right, 16px))",
                },
                zIndex: 25,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {headerRightSlot}
              <IconButton
                onClick={onClose}
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "50%",
                  backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(12px)",
                  color: darkMode ? "var(--neutralWhite)" : "#111827",
                  boxShadow: darkMode
                    ? "0 4px 16px rgba(0, 0, 0, 0.3)"
                    : "0 4px 16px rgba(0, 0, 0, 0.15)",
                  border: darkMode
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.05)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  flexShrink: 0,
                  minWidth: { xs: 40, sm: 44 },
                  minHeight: { xs: 40, sm: 44 },
                  "&:hover": {
                    backgroundColor: darkMode
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(255, 255, 255, 1)",
                    transform: "scale(1.1) rotate(90deg)",
                    boxShadow: darkMode
                      ? "0 6px 24px rgba(0, 0, 0, 0.4)"
                      : "0 6px 24px rgba(0, 0, 0, 0.2)",
                  },
                  "&:active": {
                    transform: { xs: "scale(0.9)", sm: "scale(0.95)" },
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
              </IconButton>
            </Box>

            {/* Page Content Container */}
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transform: "translateZ(0)",
                WebkitTransform: "translateZ(0)",
              }}
            >
              <Box
                ref={pagesTrackRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  willChange: "transform",
                  zIndex: 2,
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  transformStyle: "preserve-3d",
                  WebkitTransformStyle: "preserve-3d",
                }}
              >
                {/* Cover Page */}
                {SHOW_COVER_PAGE && isBookPagePreloaded(0, currentPage) ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: { xs: 0.5, sm: 1, md: 2 },
                      py: { xs: 1, sm: 3 },
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      transform: "translateZ(0)",
                      pointerEvents: currentPage === 0 ? "auto" : "none",
                    }}
                  >
                    {renderCoverPage()}
                  </Box>
                ) : null}

                {/* Content Pages — mount active ±1 only, no empty off-screen slots */}
                {allPages.map((page, index) => {
                  const bookIndex = (SHOW_COVER_PAGE ? 1 : 0) + index;
                  if (!isBookPagePreloaded(bookIndex, currentPage)) return null;

                  return (
                    <Box
                      key={`page-${page.type}-${page.order}-${index}`}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: `${bookIndex * 100}%`,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: { xs: 0, sm: 2, md: 3 },
                        py: { xs: 0, sm: 3 },
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "translateZ(0)",
                        pointerEvents: currentPage === bookIndex ? "auto" : "none",
                      }}
                    >
                      <Box
                        data-impact-book-scroll={currentPage === bookIndex ? "active" : undefined}
                        sx={{
                          width: "100%",
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                          WebkitOverflowScrolling: "touch",
                          position: "relative",
                          zIndex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: isImpactCard ? "flex-start" : "center",
                          containerType: isImpactCard ? "size" : undefined,
                          scrollSnapType: isImpactCard ? "y mandatory" : undefined,
                          backfaceVisibility: "hidden",
                          WebkitBackfaceVisibility: "hidden",
                          transform: "translateZ(0)",
                          "&::-webkit-scrollbar": {
                            width: 6,
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                            borderRadius: 3,
                            "&:hover": {
                              background: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                            },
                          },
                        }}
                      >
                        {renderContentPage(page, index)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Navigation Arrows */}
            {currentPage > 0 && (
              <IconButton
                onClick={goToPreviousPage}
                sx={{
                  position: "absolute",
                  left: {
                    xs: "max(8px, env(safe-area-inset-left, 8px))",
                    sm: "max(12px, env(safe-area-inset-left, 12px))",
                  },
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  minWidth: { xs: 40, sm: 48 },
                  minHeight: { xs: 40, sm: 48 },
                  background: darkMode ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(8px)",
                  color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                  boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.08)",
                  border: darkMode
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "1px solid rgba(0,0,0,0.08)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  zIndex: 15,
                  opacity: { xs: 0.8, sm: 0.6 },
                  "&:hover": {
                    background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.3)",
                    color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                    opacity: 1,
                    transform: "translateY(-50%) scale(1.1)",
                    boxShadow: darkMode
                      ? "0 4px 16px rgba(0,0,0,0.3)"
                      : "0 4px 16px rgba(0,0,0,0.15)",
                  },
                  "&:active": {
                    transform: {
                      xs: "translateY(-50%) scale(0.9)",
                      sm: "translateY(-50%) scale(0.95)",
                    },
                  },
                }}
              >
                <ArrowBackIosIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              </IconButton>
            )}

            {currentPage < maxNavigablePage && (
              <IconButton
                onClick={goToNextPage}
                sx={{
                  position: "absolute",
                  right: {
                    xs: "max(8px, env(safe-area-inset-right, 8px))",
                    sm: "max(12px, env(safe-area-inset-right, 12px))",
                  },
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  minWidth: { xs: 40, sm: 48 },
                  minHeight: { xs: 40, sm: 48 },
                  background: darkMode ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(8px)",
                  color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                  boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.08)",
                  border: darkMode
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "1px solid rgba(0,0,0,0.08)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  zIndex: 15,
                  opacity: { xs: 0.8, sm: 0.6 },
                  "&:hover": {
                    background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.3)",
                    color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                    opacity: 1,
                    transform: "translateY(-50%) scale(1.1)",
                    boxShadow: darkMode
                      ? "0 4px 16px rgba(0,0,0,0.3)"
                      : "0 4px 16px rgba(0,0,0,0.15)",
                  },
                  "&:active": {
                    transform: {
                      xs: "translateY(-50%) scale(0.9)",
                      sm: "translateY(-50%) scale(0.95)",
                    },
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              </IconButton>
            )}

            {/* Page Indicator */}
            <Box
              sx={{
                position: "absolute",
                bottom: {
                  xs: "max(8px, calc(8px + env(safe-area-inset-bottom, 0px)))",
                  sm: "max(20px, calc(20px + env(safe-area-inset-bottom, 0px)))",
                },
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5 },
                zIndex: 15,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                borderRadius: { xs: 4, sm: 6 },
                background: darkMode ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.7)",
                backdropFilter: "blur(12px)",
                border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
                boxShadow: darkMode ? "0 4px 16px rgba(0,0,0,0.3)" : "0 4px 16px rgba(0,0,0,0.1)",
                transition: "bottom 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
              {Array.from({ length: totalPages }).map((_, index) => (
                <Box
                  key={index}
                  role="button"
                  aria-label={
                    currentPage === index &&
                    currentPage !== tocBookPageIndex &&
                    totalPages > tocBookPageIndex
                      ? "Back to table of contents"
                      : `Go to page ${index + 1}`
                  }
                  onClick={() => {
                    if (index > maxNavigablePage) return;
                    if (
                      currentPage === index &&
                      currentPage !== tocBookPageIndex &&
                      totalPages > tocBookPageIndex
                    ) {
                      handleTocSelectPage(tocBookPageIndex);
                      return;
                    }
                    handleTocSelectPage(index);
                  }}
                  sx={{
                    width: currentPage === index ? { xs: 24, sm: 32 } : { xs: 6, sm: 8 },
                    height: { xs: 6, sm: 8 },
                    borderRadius: { xs: 3, sm: 4 },
                    background:
                      currentPage === index
                        ? "linear-gradient(135deg, var(--primary-600), var(--primary-500))"
                        : index > maxNavigablePage
                          ? darkMode
                            ? "color-mix(in srgb, var(--neutralWhite) 12%, transparent)"
                            : "color-mix(in srgb, var(--neutralBlack) 12%, transparent)"
                          : darkMode
                            ? "color-mix(in srgb, var(--neutralWhite) 25%, transparent)"
                            : "color-mix(in srgb, var(--neutralBlack) 25%, transparent)",
                    cursor: index <= maxNavigablePage ? "pointer" : "default",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow:
                      currentPage === index
                        ? "0 2px 8px color-mix(in srgb, var(--primary-600) 35%, transparent)"
                        : "none",
                    minWidth: { xs: 6, sm: 8 },
                    minHeight: { xs: 6, sm: 8 },
                    "&:hover": {
                      background:
                        currentPage === index
                          ? "linear-gradient(135deg, var(--primary-500), var(--primary-400))"
                          : darkMode
                            ? "color-mix(in srgb, var(--neutralWhite) 35%, transparent)"
                            : "color-mix(in srgb, var(--neutralBlack) 35%, transparent)",
                      transform: "scale(1.2)",
                    },
                    "&:active": {
                      transform: "scale(0.9)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Fade>
      </Modal>
      <DeckCardGuestLoginDialog open={loginDialogOpen} onClose={() => setLoginDialogOpen(false)} />
    </>
  );
};

const CardPreviewModal: React.FC<CardPreviewModalProps> = (props) => {
  const cardId = Number(props.cardData?.id) || 0;
  const impactContentId = findImpactShellPageId(flattenImpactPages(props.pages));

  return (
    <AnchorCubeProvider cardId={cardId} pages={props.pages} impactContentId={impactContentId}>
      <CardPreviewModalContent {...props} />
    </AnchorCubeProvider>
  );
};

export default CardPreviewModal;
